import PlainDate from '@Domain/PlainDate';
import aiCostsFiltersSignal from '@UI/Pages/Administration/AiCosts/Filters/Signals/aiCostsFiltersSignal';
import isCurrentMonthPeriod from '@UI/Pages/Administration/AiCosts/Helpers/isCurrentMonthPeriod';
import useAiCostLimits from '@UI/Pages/Administration/Shared/Hooks/useAiCostLimits';
import useAiCostLimitSubjects from '@UI/Hooks/useAiCostLimitSubjects';
import subjectLimitLevels from '@UI/Pages/Administration/AiCosts/CostTabs/SubjectCosts/Helpers/subjectLimitLevels';
import useCanManageLimitLevel from '@UI/Pages/Administration/AiCosts/CostTabs/SubjectCosts/Hooks/useCanManageLimitLevel';
import CostCard from '@UI/Pages/Administration/AiCosts/CostTabs/Shared/CostCard';
import CostListState from '@UI/Pages/Administration/AiCosts/CostTabs/Shared/CostListState';
import useAiCostBreakdown from '@UI/Pages/Administration/AiCosts/CostTabs/Shared/Hooks/useAiCostBreakdown';
import useAiCostSummaries from '@UI/Pages/Administration/AiCosts/CostTabs/Shared/Hooks/useAiCostSummaries';
import ShowMoreButton from '@UI/Pages/Administration/AiCosts/CostTabs/Shared/ShowMoreButton';
import subjectDimensionLabels, {
	type SubjectDimension,
} from '@UI/Pages/Administration/AiCosts/CostTabs/SubjectCosts/Helpers/subjectDimensionLabels';
import SubjectCostCards from '@UI/Pages/Administration/AiCosts/CostTabs/SubjectCosts/SubjectCostCards';
import SubjectCostsTable from '@UI/Pages/Administration/AiCosts/CostTabs/SubjectCosts/SubjectCostsTable';
import useIsCompactViewport from '@Guidelines/Component/FilterPanel/Hooks/useIsCompactViewport';
import * as React from 'react';
import { useSignalValue } from '@verclam/pulsar';

const ROWS_PER_PAGE = 200;

type Props = {
	readonly accountId: number;
	readonly dimension: SubjectDimension;
};

/**
 * Costs per facility, structure or user. Mounted under the query key of its tab. The limit column
 * appears once the limits and the manageable subjects are both loaded, for those who may manage them.
 */
const SubjectCosts: React.FC<Props> = ({ accountId, dimension }) => {
	const { list, error, refetch, showMore, isLoadingMore } = useAiCostBreakdown(
		accountId,
		dimension,
		ROWS_PER_PAGE,
	);
	const { total, reference } = useAiCostSummaries(accountId);
	const isCompact = useIsCompactViewport();
	const labels = subjectDimensionLabels[dimension];
	const filters = useSignalValue(aiCostsFiltersSignal);
	const canManageLimits = useCanManageLimitLevel(subjectLimitLevels[dimension]);
	const { data: loadedLimits } = useAiCostLimits(accountId, canManageLimits);
	const { data: loadedSubjects } = useAiCostLimitSubjects(accountId, canManageLimits);
	const showsConsumption = isCurrentMonthPeriod(filters, PlainDate.today());
	const hasLimitColumn = canManageLimits && loadedLimits !== null && loadedSubjects !== null;
	const limits = hasLimitColumn ? loadedLimits : null;
	const subjects = hasLimitColumn ? loadedSubjects : null;

	return (
		<CostCard
			title={labels.title}
			subtitle="Trié par total décroissant : ce qui coûte le plus en premier. Moyennes et écarts comparés à l'ensemble de la période."
			hint={labels.hint}
		>
			<CostListState list={list} error={error} onRetry={refetch}>
				{(loaded) => (
					<React.Fragment>
						{isCompact ? (
							<SubjectCostCards
								list={loaded}
								dimension={dimension}
								total={total}
								reference={reference}
								limits={limits}
								subjects={subjects}
								showsConsumption={showsConsumption}
							/>
						) : (
							<SubjectCostsTable
								list={loaded}
								dimension={dimension}
								total={total}
								reference={reference}
								limits={limits}
								subjects={subjects}
								showsConsumption={showsConsumption}
							/>
						)}
						<ShowMoreButton
							hasMore={loaded.hasMore}
							remainingCount={loaded.remainingCount}
							rows={ROWS_PER_PAGE}
							isLoading={isLoadingMore}
							onShowMore={showMore}
						/>
					</React.Fragment>
				)}
			</CostListState>
		</CostCard>
	);
};

export default SubjectCosts;
